import Image from "next/image";
import { formatDate } from "@/lib/format";
import { media } from "@/lib/media";
import type { FlexibleTestimonial } from "@/lib/dto";
import {
  testimonialBody,
  testimonialInitials,
  testimonialName,
  testimonialRating,
  testimonialTitle,
} from "@/lib/testimonial";
import { Star } from "lucide-react";

function RatingStars({ rating }: { rating: number }) {
  const activeStars = Math.max(1, Math.min(5, Math.round(rating)));
  return (
    <span
      className="inline-flex items-center gap-0.5"
      role="img"
      aria-label={`${rating.toFixed(1)} out of 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          fill="currentColor"
          stroke="none"
          className={i < activeStars ? "text-[#FBBC09]" : "text-line"}
        />
      ))}
    </span>
  );
}

/** Horizontal header/body/footer review card used on the testimonials
 *  listing page — a single-column list layout, distinct from ReviewCard's
 *  vertical grid-cell card used elsewhere (About Us, team detail). */
export default function ReviewCardList({
  renderData,
}: {
  renderData: FlexibleTestimonial;
}) {
  const t = renderData;
  const name = testimonialName(t);
  const ratingValue = testimonialRating(t) ?? 5;
  const avatar = media(t.avatar ?? t.image ?? null);
  const body = testimonialBody(t);
  const date = t.date ?? t.published_at;

  return (
    <div className="rounded-md border border-line bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <RatingStars rating={ratingValue} />
      </div>
      <h3 className="mt-3 line-clamp-1 font-display text-[1.125rem] font-bold text-headings">
        {testimonialTitle(t)}
      </h3>

      {body ? (
        <div className="mt-4 max-h-[100px] overflow-y-auto pr-1 text-sm leading-[1.6] text-text">
          {body}
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center pt-6 border-t border-line">
        <span className="relative grid size-12 shrink-0 place-items-center overflow-hidden rounded-full bg-primary font-mono text-xs font-semibold text-white">
          {avatar ? (
            <Image
              src={avatar.src}
              alt={avatar.alt || name}
              fill
              sizes="48px"
              className="object-cover"
            />
          ) : (
            testimonialInitials(name)
          )}
        </span>
        <span className="pl-2.5 leading-[1]">
          <span className="block text-base font-medium text-headings">
            {name}
          </span>
          {date ? (
            <span className="mt-1.5 block text-xs font-medium text-muted">
              On {formatDate(date)}
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
}
